import React from "react"
// MUI
import {Box, Chip, Tooltip, Typography} from "@mui/material"
import {FilterAltRounded} from "@mui/icons-material"
// Translation
import {useTranslation} from "react-i18next"
// Utils
import {FilterPreset, presetLongLabel, presetShortLabel} from "@/utils/filterPresets"
// Tokens
import {packagingTokens as tk} from "@/theme/packagingTokens"

// ─── Shared tooltip body: title, one line per preset, scope hint ───

type HintProps = {
    presets: FilterPreset[]
    // "brand": the list applies to every range without its own filters.
    // "range": the list is the range's own and replaces the brand's.
    scope: "brand" | "range"
    brandName?: string
}

export function PropagatedFiltersHint({presets, scope, brandName}: HintProps) {
    const {t} = useTranslation(["common"])
    return (
        <Box sx={{py: 0.25, maxWidth: 260}}>
            <Typography sx={{fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", opacity: 0.8, mb: 0.5}}>
                {t("propagated_filters")}
            </Typography>
            {presets.map((p, i) => (
                <Typography key={i} sx={{fontSize: "12px", lineHeight: 1.5}}>
                    {presetLongLabel(p)}
                </Typography>
            ))}
            <Typography sx={{fontSize: "11px", opacity: 0.75, mt: 0.5}}>
                {scope === "brand"
                    ? t("propagated_filters_brand_hint")
                    : t("propagated_filters_range_hint", {brand: brandName ?? ""})}
            </Typography>
        </Box>
    )
}

// ─── Brand card: filter-icon chip with the preset count, next to the ranges badge ───

export function PropagatedFiltersChip({presets}: {presets?: FilterPreset[]}) {
    const {t} = useTranslation(["common"])
    if (!presets || presets.length === 0) return null
    return (
        <Tooltip arrow placement={"top"} title={<PropagatedFiltersHint presets={presets} scope={"brand"} />}>
            <Chip
                icon={<FilterAltRounded />}
                label={presets.length}
                size={"small"}
                aria-label={t("propagated_filters")}
                data-testid={"propagated-filters-chip"}
                sx={{
                    height: 22,
                    borderRadius: tk.radius.pill,
                    backgroundColor: tk.color.purple100,
                    color: tk.color.purple,
                    fontSize: "11.5px",
                    fontWeight: 600,
                    cursor: "default",
                    "& .MuiChip-icon": {fontSize: 14, color: tk.color.purple, ml: "6px", mr: "-4px"},
                    "& .MuiChip-label": {px: "8px"},
                }}
            />
        </Tooltip>
    )
}

// ─── Range chip: filter icon left of the name, for ranges with their own override ───

export function PropagatedFiltersIcon({presets, brandName}: {presets?: FilterPreset[]; brandName?: string}) {
    const {t} = useTranslation(["common"])
    if (!presets || presets.length === 0) return null
    return (
        <Tooltip arrow placement={"top"} title={<PropagatedFiltersHint presets={presets} scope={"range"} brandName={brandName} />}>
            <Box
                component={"span"}
                role={"img"}
                aria-label={t("propagated_filters")}
                data-testid={"propagated-filters-icon"}
                sx={{display: "inline-flex", alignItems: "center", color: tk.color.purple, ml: "6px", mr: "-4px"}}
            >
                <FilterAltRounded sx={{fontSize: 13}} />
            </Box>
        </Tooltip>
    )
}

// ─── Inline preset chips, for the synthesis list ───

export function PresetChips({presets, max = 4}: {presets: FilterPreset[]; max?: number}) {
    const visible = presets.slice(0, max)
    const hidden = presets.slice(max)
    const chipSx = {
        height: 20,
        borderRadius: tk.radius.pill,
        backgroundColor: tk.color.bg,
        border: `1px solid ${tk.color.borderLight}`,
        fontSize: "11px",
        fontWeight: 500,
        color: tk.color.textSecondary,
        "& .MuiChip-label": {px: "7px"},
    }
    return (
        <Box sx={{display: "inline-flex", flexWrap: "wrap", gap: 0.5, alignItems: "center"}}>
            {visible.map((p, i) => (
                <Tooltip key={i} arrow placement={"top"} title={presetLongLabel(p)}>
                    <Chip label={presetShortLabel(p)} size={"small"} sx={chipSx} />
                </Tooltip>
            ))}
            {hidden.length > 0 && (
                <Tooltip
                    arrow
                    placement={"top"}
                    title={
                        <Box sx={{py: 0.25}}>
                            {hidden.map((p, i) => (
                                <Typography key={i} sx={{fontSize: "12px"}}>{presetLongLabel(p)}</Typography>
                            ))}
                        </Box>
                    }
                >
                    <Chip label={`+${hidden.length}`} size={"small"} sx={{...chipSx, border: `1px dashed ${tk.color.border}`, color: tk.color.textTertiary}} />
                </Tooltip>
            )}
        </Box>
    )
}
